
import React, { useRef, useCallback, useMemo }from 'react';
import styled from 'styled-components';
import { AgGridReact } from 'ag-grid-react';
import { AgGridReact as AgGridReactType } from 'ag-grid-react';
import { type ColDef, themeAlpine } from 'ag-grid-community';
import '@process-advisor/process-miner/shared/gridStyles.css'
import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';
import { Event } from '@process-advisor/process-miner/api/events';

import { openProgressModal } from '@process-advisor/process-miner/api/methods/modals/openProgressModal';
import { closeProgressModal } from '@process-advisor/process-miner/api/methods/modals/closeProgressModal';

const Wrapper = styled.div<{ height: string }>`
    height: ${({ height }) => height};
    width: 100%;
    margin-bottom: 5px;
`;

type SingleRow = {
    id: number;
    [key: string]: any
}

type Props = {
    rows: SingleRow[];
    columns: ColDef[];
    height: string;
    setApplyEnabled: (value: boolean) => void;
    selectionMode,
}

export const DataGrid: React.FC<Props> = ({
    rows,
    columns,
    height,
    setApplyEnabled,
    selectionMode,

}) => {
   
    const sleep = ms => new Promise(r => setTimeout(r, ms));
    const grid = useRef<AgGridReactType>(null);
    const getContextMenuItems = useCallback((params) => {
        var result = [
            {
                name: 'Insights',
                subMenu: [
                {
                    // custom item
                    name: 'Clusters',
                    action: async function () {
                        openProgressModal({status: 'Processing Clusters'})
                        await sleep(200) // needed to display Progress
                        await apiProvider.generateTasksFilteredArray({variantPaneVariants: false, type: 'activity', variant: 0,  activity: params.node.data.activity_id, from: 0, to: 0})
                        apiProvider.setDiagramCluster({source: 'activity'})
                        closeProgressModal()
                    },
                },          
                {
                    // custom item
                    name: 'Influencers',
                    action: async function () {
                        openProgressModal({status: 'Processing Influencer Insights'})
                        await sleep(200) // needed to display Progress
                        await apiProvider.generateTasksFilteredArray({variantPaneVariants: false, type: 'activity', variant: 0,  activity: params.node.data.activity_id, from: 0, to: 0})
                        apiProvider.setDiagramInfluenceAnalysis({source: 'activity'})
                        closeProgressModal()
                    },
                },    
                {
                    // custom item
                    name: 'Decompose',
                    action: function () {
                        apiProvider.generateTasksFilteredArray({variantPaneVariants: false, type: 'activity', variant: 0,  activity: params.node.data.activity_id, from: 0, to: 0})
                        apiProvider.setDiagramDecompositionTree({source: 'activity'})
                    },
                },
                {
                    name: 'Activity Analysis',
                    action: function () {
                        apiProvider.generateTasksFilteredArray({variantPaneVariants: false, type: 'activity', variant: 0,  activity: params.node.data.activity_id, from: 0, to: 0})
                        apiProvider.setTimeInActivitySource({source : 'activity'})
                        apiProvider.setAction_Time({});                        
                    },
                },                 
                {
                    name: 'Histogram',
                    action: function () {
                        apiProvider.generateHistogramArray({variantPaneVariants: false, type: 'activity-tasks', variant: 0,  activity: params.node.data.activity_id, from: 0, to: 0})
                        apiProvider.histogram({})
                    },
                },
                     

                ]
            },
            {
            name: apiProvider.getEntityUpper() + 's',
            subMenu: [
                {
                    name: 'Detail',
                    action: function () {
                        apiProvider.setActivityDetailProps({activity: params.node.data.activity_id })
                        apiProvider.taskDetail({})
                    },
                },
                {
                    name: 'History',   
                    action: function () {
                        apiProvider.processAnalytics({activity: params.node.data.activity_id, object_type: 'Activity', object_name: params.node.data.step, series_name: apiProvider.getEntityUpper() + 's'})
                    },
                },      
                {
                    name: 'LifeCycle',
                    action: function () {
                        apiProvider.generateTasksFilteredArray({variantPaneVariants: false, type: 'activity', variant: 0,  activity: params.node.data.activity_id, from: 0, to: 0})
                        apiProvider.setTasksVisualizationSource({source: 'activity'})
                        apiProvider.tasksVisualization()
                        apiProvider.taskLifecycle({})
                    },
                },

            ],
          },  
          {
            name: 'Events',
            subMenu: [
                {
                    name: 'Detail',
                    action: function () {
                        apiProvider.setActivityDetailProps({activity: params.node.data.activity_id })
                        apiProvider.eventDetail({})
                    },
                },
                {
                    name: 'Histogram',
                    action: function () {
                        apiProvider.generateHistogramArray({variantPaneVariants: false, type: 'activity-events', variant: 0,  activity: params.node.data.activity_id, from: 0, to: 0})
                        apiProvider.histogram({})
                    },
                },

            ],
        },        
    
  
          'separator',
          {
            name: 'Export',
            subMenu: [
                {
                    name: 'CSV Export (all)',
                    action: () => params.api.exportDataAsCsv(
                        {onlySelected: false}
                      ),
                },
                {
                    name: 'CSV Export (selected)',
                    action: () => params.api.exportDataAsCsv(
                        {onlySelected: true}
                      ),
                },
                {
                    name: 'XLS Export (all)',
                    action: () => params.api.exportDataAsExcel(
                        {onlySelected: false}
                      ),
                },
                {
                    name: 'XLS Export (selected)',
                    action: () => params.api.exportDataAsExcel(
                        {onlySelected: true}
                      ),
                },
            ],
        },        
        ];
        return result;
      }, []);    

    const onFirstDataRendered  = (params) => {
        if (apiProvider.getActivityFilterGridState()!= null) params.api.applyColumnState({state: apiProvider.getActivityFilterGridState()});
    };

    const onSelectionChanged = (params) => {
        apiProvider.setActivityFilterSelectedData({
            selection: params.api.getSelectedRows(),
        });
        setApplyEnabled(false);
    };

    React.useEffect(() => {
        const listener = () => {
            if (grid != null) {
                grid.current.api.forEachNode((rowNode) => {rowNode.setSelected(false)});
            };
        };
        apiProvider.on(Event.resetFilter, listener);
  
        return () => {
            apiProvider.off(Event.resetFilter, listener);
        };
    }, []);

    const onSaveGridColumnState = (params) => {
        let columnState = params.api.getColumnState();
        apiProvider.saveActivityFilterGridState({columnState})
    }

    const getRowHeight = () => 25;
    return (
        <Wrapper height={height}>
            <div
                
                style={{
                    height: '100%',
                    width: '100%',
                    fontSize: '12px',
                    fontFamily: 'system-ui'
,
                }}>
                {selectionMode == 'multiple' ?

                <AgGridReact theme={themeAlpine} 
                    rowSelection="multiple"
                    onSelectionChanged={onSelectionChanged}
                    onFirstDataRendered={onFirstDataRendered}
                    getContextMenuItems={getContextMenuItems}
                    getRowHeight={getRowHeight}

                    onFilterChanged={onSaveGridColumnState}
                    onColumnVisible={onSaveGridColumnState}
                    onColumnResized={onSaveGridColumnState}
                    onSortChanged={onSaveGridColumnState}

                    columnDefs={columns}
                    headerHeight={40}
                    ref={grid}
                    defaultColDef={{
                        sortable: true,
                        filter: true,
                        resizable: true,
                        wrapText: true,
                        autoHeight: true,
                    }}
                    sideBar={
                        {
                            toolPanels: [
                                {
                                    id: 'columns',
                                    labelDefault: 'Columns',
                                    labelKey: 'columns',
                                    iconKey: 'columns',
                                    toolPanel: 'agColumnsToolPanel',
                                    toolPanelParams: {
                                        suppressRowGroups: true,
                                        suppressValues: true,
                                        suppressPivotMode: true, 
                                    }
                      
                                },
                                {
                                    id: 'filters',
                                    labelDefault: 'Filters',
                                    labelKey: 'filters',
                                    iconKey: 'filter',
                                    toolPanel: 'agFiltersToolPanel',
                                },
                            ],
                            hiddenByDefault: false,
                            defaultToolPanel: '',
                        }
                    }

                    enableCharts={true}
                    chartThemes={[
                        'ag-solar',
                        'ag-pastel',
                        'ag-material-dark',
                        'ag-vivid-dark',
                    ]}
                    animateRows={true}
                    rowData={rows} />
                : 
                <AgGridReact theme={themeAlpine} 
                rowSelection="single"
                onSelectionChanged={onSelectionChanged}
                onFirstDataRendered={onFirstDataRendered}
                getContextMenuItems={getContextMenuItems}
                getRowHeight={getRowHeight}

                onFilterChanged={onSaveGridColumnState}
                onColumnVisible={onSaveGridColumnState}
                onColumnResized={onSaveGridColumnState}
                onSortChanged={onSaveGridColumnState}

                columnDefs={columns}
                headerHeight={40}
                ref={grid}
                defaultColDef={{
                    sortable: true,
                    filter: true,
                    resizable: true,
                    wrapText: true,
                    autoHeight: true,
                }}
                sideBar={
                    {
                        toolPanels: [
                            {
                                id: 'columns',
                                labelDefault: 'Columns',
                                labelKey: 'columns',
                                iconKey: 'columns',
                                toolPanel: 'agColumnsToolPanel',
                                toolPanelParams: {
                                    suppressRowGroups: true,
                                    suppressValues: true,
                                    suppressPivotMode: true, 
                                }
                  
                            },
                            {
                                id: 'filters',
                                labelDefault: 'Filters',
                                labelKey: 'filters',
                                iconKey: 'filter',
                                toolPanel: 'agFiltersToolPanel',
                            },
                        ],
                        hiddenByDefault: false,
                        defaultToolPanel: '',
                    }
                }

                enableCharts={true}
                chartThemes={[
                    'ag-solar',
                    'ag-pastel',
                    'ag-material-dark',
                    'ag-vivid-dark',
                ]}
                animateRows={true}
                rowData={rows} />
}
            </div>
        </Wrapper>
    );
};
